import { z } from '../zod.js';
import {
  CursorPaginationValidationBaseSchema,
  SellerAPICursorPaginationValidationSchema,
  withCursorPaginationValidation,
} from '../shared/cursorPagination';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import {
  LivemodeFilterValidationSchema,
  LivemodeQueryParamValidationSchema,
} from '../shared/livemode';
import {
  TICKET_CHECK_IN_STATUS,
  TICKET_STATUS,
  TicketCheckInStatusValidationEnum,
  TicketStatusValidationEnum,
} from './enum';

const ticketListFilterFields = {
  search: z.string().optional().meta({
    description:
      'Case-insensitive search matched against `attendeeEmail`, `attendeeName`, `ticketShortId` and `orderId`. The query is routed based on input shape: email-like inputs (`jane@example.com`) match `attendeeEmail` only; an 8-digit input (`64819115`) is treated as a whole order reference and matched exactly against `orderId`; other identifier-like inputs containing a digit, underscore or hyphen match `ticketShortId` and `orderId`; multi-word inputs match `attendeeName` and `ticketShortId`; any other single bare word falls back to a broad substring match across `attendeeName`, `attendeeEmail` and `ticketShortId`. Best default when the guest is identified by name, email, or the code on their ticket. A booking produces one ticket per attendee - always review all matches, not just the first.',
  }),
  pageId: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Return only tickets for a single event page. Use this to build a guest list for one event.',
  }),
  bookingId: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Return the tickets created by one booking. Use when you already have the booking from the bookings endpoint and want the individual attendee tickets.',
  }),
  orderId: z.string().optional().meta({
    description:
      'Exact match on the order reference shared by every ticket in a booking. Partial values do not match — use `search` for that.',
  }),
  customerId: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Return every ticket booked by one customer, across events. Useful when you already have the customer record.',
  }),
  ticketTypeId: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Return only tickets of one ticket type (e.g. VIP). Combine with `pageId` when the same type name exists on several events.',
  }),
  status: TicketStatusValidationEnum.optional().meta({
    description: `Filter by ticket status. Valid values: ${Object.values(TICKET_STATUS).join(', ')}. Use "PAID" for tickets valid for admission and "CANCELED" for voided ones. An unfiltered list includes both.`,
  }),
  checkInStatus: TicketCheckInStatusValidationEnum.optional().meta({
    description: `Filter by check-in state. Valid values: ${Object.values(TICKET_CHECK_IN_STATUS).join(', ')}. "CHECKEDIN" returns guests currently admitted; "UNCHECKEDIN" returns both guests who were checked back out and guests who have never been checked in.`,
  }),
  createdAfter: z.iso.datetime().optional().meta({
    description:
      'Inclusive lower bound on ticket createdAt. ISO-8601 UTC datetime with the `Z` suffix (e.g. `2026-01-01T00:00:00Z`). Never use a local timezone offset — tickets are stored and compared in UTC.',
  }),
  createdBefore: z.iso.datetime().optional().meta({
    description:
      'Inclusive upper bound on ticket createdAt. ISO-8601 UTC datetime with the `Z` suffix (e.g. `2026-12-31T23:59:59Z`). Never use a local timezone offset — tickets are stored and compared in UTC.',
  }),
} as const;

// Two list-schema variants, one filter set: the base variant takes native zod
// types (`limit: number`, `livemode: boolean`) for JSON callers such as the MCP tool,
// the seller-API variant coerces both from the URL query string for the Koa controller.
export const TicketListQueryValidationSchema = withCursorPaginationValidation(
  CursorPaginationValidationBaseSchema.extend({
    ...ticketListFilterFields,
    livemode: LivemodeFilterValidationSchema,
  }),
);

export const TicketListSellerAPIQueryValidationSchema = withCursorPaginationValidation(
  SellerAPICursorPaginationValidationSchema.extend({
    ...ticketListFilterFields,
    livemode: LivemodeQueryParamValidationSchema,
  }),
);
